import React, { useEffect, useState, useCallback, useMemo } from "react";
import "../../Util.css";
import "../Style.css";
import Accordion from "react-bootstrap/Accordion";
import logo from "../../assets/svg/vehicle-logo.svg";
import dashboard from "../../assets/Sidebar/dashboard.png";
import dashboard_light from "../../assets/Sidebar/dashboard_light.png";
import location from "../../assets/Sidebar/location.png";
import location_light from "../../assets/Sidebar/location_light.png";
import vehicle from "../../assets/Sidebar/vehicle.png";
import vehicle_light from "../../assets/Sidebar/vehicle_light.png";
import service from "../../assets/Sidebar/service.png";
import service_light from "../../assets/Sidebar/service_light.svg";
import renewal from "../../assets/Sidebar/renewals.png";
import renewal_light from "../../assets/Sidebar/renewal_light.svg";
import inspection from "../../assets/Sidebar/inspection.png";
import inspection_light from "../../assets/Sidebar/inspection_light.svg";
import issue from "../../assets/Sidebar/issue.png";
import issue_light from "../../assets/Sidebar/issue_light.svg";
import vendor from "../../assets/Sidebar/vendors.png";
import vendor_light from "../../assets/Sidebar/vendor_light.svg";
import reportIcon from "../../assets/svg/reportIc.svg";
import report from "../../assets/Sidebar/report.svg";
import report_light from "../../assets/Sidebar/report_light.svg";
import Button from "react-bootstrap/Button";
import Offcanvas from "react-bootstrap/Offcanvas";
import { Menu } from "lucide-react";
import { useNavigate, useLocation } from "react-router-dom";
import { useGlobal } from "../../GlobalContext.js";

// --- Constants ---
const VEHICLE_BUTTONS = [
  "Vehicle list",
  "Vehicle assignment",
  "Km history",
  "Expenses history",
  "Fuel history",
];
const SERVICES_BUTTONS = ["Service", "Work Orders", "Service Reminders"];
const RENEWAL_BUTTONS = ["Renewals", "Upcoming Reminder"];

export const Sidebar = ({ currentFile }) => {
  const { rolePermissionList, orgImage } = useGlobal();
  const navigate = useNavigate();
  const active_location = useLocation();

  // --- State ---
  const [activeBtn, setActiveBtn] = useState(currentFile ?? "");

  const getInitialKey = useCallback(() => {
    if (VEHICLE_BUTTONS.includes(currentFile)) return "0";
    if (SERVICES_BUTTONS.includes(currentFile)) return "1";
    if (RENEWAL_BUTTONS.includes(currentFile)) return "2";
    return null;
  }, [currentFile]);

  const [openKey, setOpenKey] = useState(getInitialKey);
  const [show, setShow] = useState(false);
  // eslint-disable-next-line
  const [activeIndexBtn, setActiveIndexBtn] = useState("0");

  // --- Helpers & Callbacks ---
  const activeVehicleIndex = useMemo(
    () => VEHICLE_BUTTONS.indexOf(activeBtn),
    [activeBtn],
  );
  const activeServicesIndex = useMemo(
    () => SERVICES_BUTTONS.indexOf(activeBtn),
    [activeBtn],
  );
  const activeRenewalIndex = useMemo(
    () => RENEWAL_BUTTONS.indexOf(activeBtn),
    [activeBtn],
  );

  const checkPermission = useCallback(
    (menuName, permission) => {
      return (
        rolePermissionList.findIndex(
          (element) =>
            element["name"] === menuName &&
            element["menu_permission"] === true &&
            element[permission] === true,
        ) > -1
      );
    },
    [rolePermissionList],
  );

  const handleClose = useCallback(() => setShow(false), []);
  const handleShow = useCallback(() => setShow(true), []);

  const handleBtnClick = useCallback((label) => {
    setActiveBtn(label);
    if (VEHICLE_BUTTONS.includes(label)) {
      setOpenKey("0");
    } else if (SERVICES_BUTTONS.includes(label)) {
      setOpenKey("1");
    } else if (RENEWAL_BUTTONS.includes(label)) {
      setOpenKey("2");
    }
  }, []);

  const getRoute = (label) => {
    switch (label) {
      case "Vehicle list":
        return "/list";
      case "Vehicle assignment":
        return "/assignment";
      case "Km history":
        return "/km";
      case "Expenses history":
        return "/expenses";
      case "Fuel history":
        return "/fuel";
      case "Service":
        return "/service";
      case "Work Orders":
        return "/workorder";
      case "Service Reminders":
        return "/reminder";
      case "Renewals":
        return "/renewal";
      case "Upcoming Reminder":
        return "/upcoming";
      default:
        return null;
    }
  };

  // --- Effects ---
  useEffect(() => {
    setActiveBtn(currentFile);
  }, [currentFile]);

  useEffect(() => {
    if (!currentFile) return;
    setActiveBtn(currentFile);
    if (VEHICLE_BUTTONS.includes(currentFile)) setOpenKey("0");
    else if (SERVICES_BUTTONS.includes(currentFile)) setOpenKey("1");
    else if (RENEWAL_BUTTONS.includes(currentFile)) setOpenKey("2");
  }, [currentFile]);

  useEffect(() => {
    const path = active_location.pathname;
    switch (path) {
      case "/dashboard":
      case "/location":
        setActiveBtn("");
        break;
      // Vehicles
      case "/list":
        setActiveIndexBtn(["0"]);
        setActiveBtn("Vehicle list");
        break;
      case "/assignment":
        setActiveIndexBtn(["0"]);
        setActiveBtn("Vehicle assignment");
        break;
      case "/km":
        setActiveIndexBtn(["0"]);
        setActiveBtn("Km history");
        break;
      case "/expenses":
        setActiveIndexBtn(["0"]);
        setActiveBtn("Expenses history");
        break;
      case "/fuel":
        setActiveIndexBtn(["0"]);
        setActiveBtn("Fuel history");
        break;
      // Services
      case "/service":
        setActiveIndexBtn(["1"]);
        setActiveBtn("Service");
        break;
      case "/workorder":
        setActiveIndexBtn(["1"]);
        setActiveBtn("Work Orders");
        break;
      case "/reminder":
        setActiveIndexBtn(["1"]);
        setActiveBtn("Service Reminders");
        break;
      // Renewals
      case "/renewal":
        setActiveIndexBtn(["2"]);
        setActiveBtn("Renewals");
        break;
      case "/upcoming":
        setActiveIndexBtn(["2"]);
        setActiveBtn("Upcoming Reminder");
        break;
      // Standalone
      case "/inspection":
        setActiveBtn("Inspection");
        break;
      case "/issue-history":
        setActiveBtn("Issue History");
        break;
      case "/vendor":
        setActiveBtn("Vendor");
        break;
      case "/report":
        setActiveBtn("Report");
        break;
      default:
        break;
    }
  }, [active_location.pathname]);

  // --- Render Helpers ---

  const renderNavItem = (
    label,
    path,
    iconDefault,
    iconActive,
    isMobile = false,
  ) => {
    const isActive = active_location.pathname === path;
    const isBtnActive = activeBtn === label;
    const isStandardActive =
      label === "Dashboard" || label === "Location detection"
        ? isActive
        : isBtnActive;

    return (
      <div
        className="d-flex"
        onClick={() => {
          if (active_location.pathname !== path) navigate(path);
          if (isMobile) handleClose();
        }}
      >
        <button
          className={`bg-transparent border-0 d-flex gap-2 align-items-center w-100 p-2 px-3 rounded-pill font-13-400-C2C2C4 ${
            isStandardActive
              ? "acc-body-btn text-muted-active fw-semibold"
              : "text-muted-white "
          }`}
        >
          <img src={isStandardActive ? iconActive : iconDefault} alt={label} />
          <span>{label}</span>
        </button>
      </div>
    );
  };

  const renderAccordionItem = (
    eventKey,
    headerLabel,
    headerIconDefault,
    headerIconActive,
    buttons,
    activeIndex,
    isMobile,
  ) => {
    const filteredButtons = buttons.filter((item) => {
      if (item === "Vehicle list")
        return checkPermission("Vehicle", "menu_permission");
      if (item === "Vehicle assignment")
        return checkPermission("Vehicle assignment", "menu_permission");
      if (item === "Km history")
        return checkPermission("KM History", "menu_permission");
      if (item === "Expenses history")
        return checkPermission("Expenses history", "menu_permission");
      if (item === "Fuel history")
        return checkPermission("Fuel History", "menu_permission");

      if (item === "Service")
        return checkPermission("Service", "menu_permission");
      if (item === "Work Orders")
        return checkPermission("Work Order", "menu_permission");
      if (item === "Service Reminders")
        return checkPermission("Service Reminders", "menu_permission");

      if (item === "Renewals")
        return checkPermission("Renewal", "menu_permission");
      if (item === "Upcoming Reminder")
        return checkPermission("Renewal", "menu_permission");
      return false;
    });

    if (filteredButtons.length === 0) return null;

    let isHeaderActive = false;
    if (headerLabel === "Vehicles") {
      isHeaderActive = [
        "/list",
        "/vehicle-create",
        "/vehicle-view",
        "/assignment",
        "/km",
        "/expenses",
        "/newExpense",
        "/fuel",
      ].includes(active_location.pathname);
    } else if (headerLabel === "Services") {
      isHeaderActive = [
        "/service",
        "/newService",
        "/viewService",
        "/serviceReminder",
        "/workorder",
      ].includes(active_location.pathname);
    } else if (headerLabel === "Renewals") {
      isHeaderActive = ["/renewal", "/upcoming", "/renewals"].includes(
        active_location.pathname,
      );
    }

    return (
      <Accordion.Item
        eventKey={eventKey}
        className="bg-transparent border-0 mb-3"
      >
        <Accordion.Header
          className={
            isHeaderActive
              ? "text-muted-active ps-3 pe-1"
              : "text-muted-white ps-3 pe-1"
          }
        >
          <img
            src={isHeaderActive ? headerIconActive : headerIconDefault}
            alt={headerLabel}
            className="m-0"
          />
          <span className="ps-2 font-14-header">{headerLabel}</span>
        </Accordion.Header>
        <Accordion.Body className="text-muted-active w-100 p-0 py-4">
          <div className="progress-bar-parent d-flex justify-content-center">
            <div className="progress-bar-visual">
              {buttons
                .filter((b) => filteredButtons.includes(b))
                .map((_, idx) => (
                  <div key={idx} className="progress-bar-step">
                    <div
                      className={`progress-bar-node${idx <= activeIndex ? " active" : ""}`}
                    ></div>
                    {idx < filteredButtons.length - 1 && (
                      <div
                        className={`progress-bar-line${idx < activeIndex ? " filled" : ""}`}
                      ></div>
                    )}
                  </div>
                ))}
            </div>
            <div className="progress-btns w-100">
              {filteredButtons.map((label) => (
                <button
                  key={label}
                  className={`bg-transparent w-100 d-flex p-2 px-3 border-0 rounded-pill font-13-400-C2C2C4 text-muted-white ${
                    activeBtn === label
                      ? "acc-body-btn text-muted-active fw-semibold"
                      : " "
                  }`}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleBtnClick(label);
                    if (isMobile) handleClose();
                    const route = getRoute(label);
                    if (route) navigate(route);
                  }}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>
        </Accordion.Body>
      </Accordion.Item>
    );
  };

  const SidebarContent = ({ isMobile, className }) => (
    <div
      className={`d-flex flex-column ${className}`}
      style={{ background: "#471070" }}
    >
      <div className="d-flex justify-content-center">
        <img
          src={orgImage || logo}
          style={isMobile ? {} : { objectFit: "contain", width: "120px" }}
          alt="logo"
          onClick={() => {
            navigate("/");
            if (isMobile) handleClose();
          }}
        />
      </div>

      {checkPermission("Dashboard", "menu_permission") &&
        renderNavItem(
          "Dashboard",
          "/dashboard",
          dashboard,
          dashboard_light,
          isMobile,
        )}

      {checkPermission("Location detection", "menu_permission") &&
        renderNavItem(
          "Location detection",
          "/location",
          location,
          location_light,
          isMobile,
        )}

      <hr className="m-0" />

      <Accordion
        activeKey={openKey}
        onSelect={(key) => setOpenKey(key)}
        className="d-flex flex-column gap-3"
      >
        {renderAccordionItem(
          "0",
          "Vehicles",
          vehicle,
          vehicle_light,
          VEHICLE_BUTTONS,
          activeVehicleIndex,
          isMobile,
        )}
        {renderAccordionItem(
          "1",
          "Services",
          service,
          service_light,
          SERVICES_BUTTONS,
          activeServicesIndex,
          isMobile,
        )}
        {renderAccordionItem(
          "2",
          "Renewals",
          renewal,
          renewal_light,
          RENEWAL_BUTTONS,
          activeRenewalIndex,
          isMobile,
        )}
      </Accordion>

      <hr className="m-0" />

      <div className="d-flex flex-column gap-4">
        {checkPermission("Inspection History", "menu_permission") && (
          <div
            className="d-flex"
            onClick={() => {
              if (active_location.pathname !== "/inspection")
                navigate("/inspection");
              if (isMobile) handleClose();
            }}
          >
            <button
              className={`bg-transparent w-100 border-0 px-3 p-2 rounded-pill d-flex gap-2 align-items-center font-13-400-C2C2C4 ${activeBtn === "Inspection" ? "acc-body-btn text-muted-active fw-semibold" : "text-muted-white "}`}
            >
              <img
                src={activeBtn === "Inspection" ? inspection_light : inspection}
                alt="inspection"
              />
              <span>Inspection history</span>
            </button>
          </div>
        )}

        {checkPermission("Issue History", "menu_permission") && (
          <div
            className="d-flex"
            onClick={() => {
              if (active_location.pathname !== "/issue-history")
                navigate("/issue-history");
              if (isMobile) handleClose();
            }}
          >
            <button
              className={`bg-transparent w-100 border-0 px-3 p-2 rounded-pill d-flex gap-2 align-items-center font-13-400-C2C2C4 ${activeBtn === "Issue History" ? "acc-body-btn text-muted-active fw-semibold" : "text-muted-white "}`}
            >
              <img
                src={activeBtn === "Issue History" ? issue_light : issue}
                alt="issue"
              />
              <span>Issue history</span>
            </button>
          </div>
        )}

        {checkPermission("Vendor", "menu_permission") && (
          <div
            className="d-flex"
            onClick={() => {
              if (active_location.pathname !== "/vendor") navigate("/vendor");
              if (isMobile) handleClose();
            }}
          >
            <button
              className={`bg-transparent w-100 border-0 px-3 p-2 rounded-pill d-flex gap-2 align-items-center font-13-400-C2C2C4 ${activeBtn === "Vendor" ? "acc-body-btn text-muted-active fw-semibold" : "text-muted-white "}`}
            >
              <img
                src={activeBtn === "Vendor" ? vendor_light : vendor}
                alt="vendor"
              />
              <span>Vendor</span>
            </button>
          </div>
        )}

        {checkPermission("Report", "menu_permission") && (
          <div
            className="d-flex"
            onClick={() => {
              if (active_location.pathname !== "/report") navigate("/report");
              if (isMobile) handleClose();
            }}
          >
            <button
              className={`bg-transparent w-100 border-0 px-3 p-2 rounded-pill d-flex gap-2 align-items-center font-13-400-C2C2C4 ${activeBtn === "Report" ? "acc-body-btn text-muted-active fw-semibold" : "text-muted-white "}`}
            >
              <img
                src={
                  activeBtn === "Report"
                    ? report_light
                    : isMobile
                      ? reportIcon
                      : report
                }
                alt="report"
              />
              <span>Report</span>
            </button>
          </div>
        )}
      </div>
    </div>
  );

  return (
    <>
      <div className="d-flex align-items-start h-100">
        <Button
          onClick={handleShow}
          className="d-flex d-xl-none m-3 bg-black border-0"
        >
          <Menu />
        </Button>
      </div>

      <Offcanvas show={show} onHide={handleClose}>
        <Offcanvas.Header closeButton style={{ background: "#471070" }}>
          <Offcanvas.Title></Offcanvas.Title>
        </Offcanvas.Header>
        <Offcanvas.Body className="p-0">
          <SidebarContent
            isMobile={true}
            className="sidebar-offcanvas p-3 border-0 gap-4"
          />
        </Offcanvas.Body>
      </Offcanvas>

      <SidebarContent
        isMobile={false}
        className="sidebar ps-2 pt-4 pe-3 gap-3 d-xl-flex d-none"
      />
    </>
  );
};
